import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiltersButton,
  FiltersField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@noor/ui';
import { FiltersModal } from '@/shared/ui/filters-modal';
import { MODERATION_SOURCE_FILTERS, MODERATION_STATUS_TABS } from '@/entities/moderation';
import type {
  ModerationCategoryOption,
  ModerationListSearch,
} from '@/entities/moderation';
import { AppliedFilters } from '@/shared/ui/applied-filters';
import { ModerationPageHeader } from './moderation-page-header';
import { DateRangePill } from './filters/date-range-pill';

interface ModerationFiltersProps {
  params: ModerationListSearch;
  onParamsChange: (next: Partial<ModerationListSearch>) => void;
}

/**
 * Moderation toolbar: the date-range pill stays inline; the status tabs now
 * live in the title-row filters modal as a select (other table pages
 * parity). Controlled — the page owns the URL params; filters apply
 * client-side (see the table).
 */
export function ModerationFilters({ params, onParamsChange }: ModerationFiltersProps) {
  return (
    <DateRangePill
      dateFrom={params.dateFrom}
      dateTo={params.dateTo}
      onParamsChange={onParamsChange}
    />
  );
}

/**
 * Queue title row + filters-modal trigger shared by the four moderation
 * queues. Inline toolbar controls (the date-range pill) ride in the title
 * row, left of the filters button, so they size to their content instead of
 * stretching a full-width row. The status select stays a local draft and
 * commits through `onParamsChange` only when Apply is clicked; Reset
 * restores 'all' while the date range keeps its own inline clear.
 */
export function ModerationQueueHeader({
  titleKey,
  toolbar,
  params,
  onParamsChange,
  productCategories = [],
  productCategoryId,
}: {
  titleKey: string;
  toolbar?: ReactNode | undefined;
  params: ModerationListSearch;
  onParamsChange: (next: Partial<ModerationListSearch>) => void;
  productCategories?: ModerationCategoryOption[];
  productCategoryId?: string | undefined;
}) {
  const { t } = useTranslation('moderation');
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <>
      <ModerationPageHeader
        titleKey={titleKey}
        actions={
          <>
            {toolbar}
            <FiltersButton
              label={t('common:filters.open', 'Open filters')}
              onClick={() => setFiltersOpen(true)}
            />
          </>
        }
      />
      <ModerationStatusModal
        // Remount on every open/close so the draft re-seeds from the
        // current URL params each time the modal opens.
        key={filtersOpen ? 'open' : 'closed'}
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        status={params.status}
        source={params.source}
        onChange={onParamsChange}
        productCategoryId={productCategoryId}
      />
      <AppliedFilters
        items={[
          ...(params.status !== 'all'
            ? [
                {
                  key: 'status',
                  label: `${t('table.status', 'Status')}: ${t(`tabs.${params.status}`, params.status)}`,
                  onRemove: () => onParamsChange({ status: 'all' }),
                },
              ]
            : []),
          ...(params.source !== 'all'
            ? [
                {
                  key: 'source',
                  label: `${t('filters.source', 'Источник')}: ${t(`source.${params.source}`, params.source)}`,
                  onRemove: () => onParamsChange({ source: 'all' }),
                },
              ]
            : []),
          ...(params.dateFrom || params.dateTo
            ? [
                {
                  key: 'date',
                  label: `${t('filters.date', 'Период подачи')}: ${[params.dateFrom, params.dateTo].filter(Boolean).join(' – ')}`,
                  onRemove: () =>
                    onParamsChange({ dateFrom: undefined, dateTo: undefined }),
                },
              ]
            : []),
          ...(params.search
            ? [
                {
                  key: 'search',
                  label: `${t('filters.search', 'Поиск')}: ${params.search}`,
                  onRemove: () => onParamsChange({ search: undefined }),
                },
              ]
            : []),
          ...(params.productCategoryId
            ? [
                {
                  key: 'productCategory',
                  label: `${t('filters.productCategory', 'Product category')}: ${productCategories.find((item) => item.id === params.productCategoryId)?.name ?? params.productCategoryId}`,
                  onRemove: () => onParamsChange({ productCategoryId: undefined }),
                },
              ]
            : []),
        ]}
      />
    </>
  );
}

// Sentinel for the "all" choice (Radix Select forbids empty values).
const ALL_VALUE = '__all__';

function ModerationStatusModal({
  open,
  onOpenChange,
  status,
  source,
  onChange,
  productCategoryId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: ModerationListSearch['status'];
  source: ModerationListSearch['source'];
  onChange: (next: Partial<ModerationListSearch>) => void;
  productCategoryId?: string | undefined;
}) {
  const { t } = useTranslation('moderation');
  // Local draft, seeded from the URL params on mount. The parent remounts
  // this modal on every open (see the `key` above), so reopening always
  // starts from the current params and edits stay local until Apply.
  const [draftStatus, setDraftStatus] = useState<ModerationListSearch['status']>(status);
  const [draftSource, setDraftSource] = useState<ModerationListSearch['source']>(source);
  const [draftCategory, setDraftCategory] = useState(productCategoryId ?? '');

  const handleApply = () => {
    onChange({
      status: draftStatus,
      source: draftSource,
      productCategoryId: draftCategory || undefined,
    });
    onOpenChange(false);
  };

  const handleReset = () => {
    setDraftStatus('all');
    setDraftSource('all');
    setDraftCategory('');
    onChange({ status: 'all', source: 'all', productCategoryId: undefined });
  };

  return (
    <FiltersModal
      open={open}
      onOpenChange={onOpenChange}
      title={t('common:filters.title', 'Filter')}
      applyLabel={t('common:filters.apply', 'Apply')}
      resetLabel={t('common:filters.reset', 'Reset')}
      onApply={handleApply}
      onReset={handleReset}
    >
      <FiltersField label={t('table.status', 'Status')}>
        <Select
          value={draftStatus === 'all' ? ALL_VALUE : draftStatus}
          onValueChange={(next) =>
            setDraftStatus(
              next === ALL_VALUE ? 'all' : (next as ModerationListSearch['status']),
            )
          }
        >
          <SelectTrigger
            aria-label={t('table.status', 'Status')}
            className="w-full min-w-0"
          >
            <SelectValue placeholder={t('table.status', 'Status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>{t('tabs.all', 'All')}</SelectItem>
            {MODERATION_STATUS_TABS.filter((status) => status !== 'all').map((status) => (
              <SelectItem key={status} value={status}>
                {t(`tabs.${status}`, status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FiltersField>
      <FiltersField label={t('filters.source', 'Источник')}>
        <Select
          value={draftSource === 'all' ? ALL_VALUE : draftSource}
          onValueChange={(next) =>
            setDraftSource(
              next === ALL_VALUE ? 'all' : (next as ModerationListSearch['source']),
            )
          }
        >
          <SelectTrigger
            aria-label={t('filters.source', 'Источник')}
            className="w-full min-w-0"
          >
            <SelectValue placeholder={t('filters.source', 'Источник')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>{t('tabs.all', 'All')}</SelectItem>
            {MODERATION_SOURCE_FILTERS.filter((value) => value !== 'all').map((value) => (
              <SelectItem key={value} value={value}>
                {t(`source.${value}`, value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FiltersField>
      {/* Temporarily disabled: product-category filtering will be re-enabled later.
      {productCategories.length > 0 && (
        <FiltersField label={t('filters.productCategory', 'Product category')}>
          <Select
            value={draftCategory || ALL_VALUE}
            onValueChange={(value) => setDraftCategory(value === ALL_VALUE ? '' : value)}
          >
            <SelectTrigger
              aria-label={t('filters.productCategory', 'Product category')}
              className="h-10 w-full min-w-0 justify-between rounded-[24px] border-filter-border bg-filter-bg [&>svg:last-child]:ml-auto"
            >
              <SelectValue placeholder={t('filters.allCategories', 'All categories')} />
            </SelectTrigger>
            <SelectContent side="bottom" avoidCollisions={false}>
              <SelectItem value={ALL_VALUE}>
                {t('filters.allCategories', 'All categories')}
              </SelectItem>
              {productCategories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FiltersField>
      )}
      */}
    </FiltersModal>
  );
}
